import APIs from "redux/apis"
import { APIResponse } from "redux/apis/types"
import {
  get,
  map,
  isArray,
  isNaN,
  includes,
  pick,
  orderBy,
  findKey,
} from "lodash"
import { Sort } from "utils/sort"
import { checkIsSp } from "utils/util"
import { pageLoadRegist } from "redux/ducks/PageLoad"
import store from "redux/store"

/************************
 * 国内株＞ランキング
 * 外国株＞ランキング　　共通
 ***********************/

//業種別株価指数選択時に表示させる空データ
const nonData = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}]
//信用系ランキングタイプ判定用
const trustNums = [
  1000,
  1001,
  1002,
  1003,
  1010,
  1011,
  1020,
  1021,
  1030,
  1031,
  1040,
  1041,
]
export const IndustryNum = 8 //業種別株価指数 Sort.RankingExchangeFull
export const IndustryTypes = [801, NaN, NaN, 802] //業種別株価指数が選択された場合のタイプ
export const IndustryTypeNums = [0, NaN, NaN, 1] //業種別株価指数が選択された場合のタイプナンバー
export const initialTypeNums = [3, 1, 2, 7]
export const initialTypeNumsOfForeign = [303, 103, 203, NaN]

const rankingType = [
  "RankingSortFull",
  "RankingSortByOther",
  "RankingSortByNikkei",
  "RankingSortByIndustry",
  "RankingSortByForeign",
]

export const rankingList = pick(Sort, rankingType)

//APIを叩くために必要なパラメタ(Type)を取得するメソッド
export const getRankingType = (
  rankingTypeNum: number,
  exchangeIndex: number,
  isForeign?: boolean
): number => {
  const currentExchange = isForeign
    ? Sort.ForeignRankingExchangeFull[exchangeIndex]
    : Sort.RankingExchangeFull[exchangeIndex]
  const exchangeName = get(currentExchange, "name")

  if (exchangeName === "業種別株価指数") {
    const exchangeNum = get(currentExchange, "rankingTypeNum")
    return Number(exchangeNum + rankingTypeNum)
  } else {
    //信用系ランキングの場合
    if (includes(trustNums, rankingTypeNum)) {
      const exchangeNum = get(currentExchange, "rankingTypeNumAsTrust")
      return Number(rankingTypeNum + exchangeNum)
      //信用系以外
    } else {
      const exchangeNum = get(currentExchange, "rankingTypeNum")
      return isForeign
        ? Number(exchangeNum + rankingTypeNum)
        : Number(rankingTypeNum + exchangeNum)
    }
  }
}

//type:actionType
type actionTypeList =
  | "INITIAL"
  | "SMARTPHONE"
  | "DISPLAY_SUMMARY"
  | "DISPLAY_DETAILS"
  | "SUMMARY_EXCHANGE"
  | "SUMMARY_EXCHANGE_TRANSITION"
  | "SUMMARY_TYPE_1"
  | "SUMMARY_TYPE_2"
  | "SUMMARY_TYPE_3"
  | "SUMMARY_TYPE_4"
  | "DETAILS_EXCHANGE"
  | "DETAILS_TYPE"
  | "DETAILS_BUTTON"
  | "DETAILS_SORT"

type rankingType = {
  type: APIResponse.Res
  num: number
  time: string
}

//type:State
type RankingState = {
  displayType: number
  summaryExchange: number
  summaryType1: rankingType | {}
  summaryType2: rankingType | {}
  summaryType3: rankingType | {}
  summaryType4: rankingType | {}
  detailsExchange: number
  detailsType: rankingType | {}
  sortStatus: Sort
}

//Type:Action
type RankingAction = {
  actionType: string
  exchange: number
  rankingData: APIResponse.Res
  rankingTypeNum: Array<number>
  sortData?: APIResponse.Res | []
  sortStatus?: Sort
}

//Type:API
type RequestApi = {
  actionType: actionTypeList
  exchange?: number
  rankingType?: number | Array<number> | {}
  rankingTypeNum?: Array<number>
  take?: Array<number>
  changeRanking: Function
  sortData?: APIResponse.Res | []
  sortStatus?: Sort
  loadResistId?: string
}

export type Status = "neutral" | "desc" | "asc" | {}

type Sort = {
  status: Status
  param: string
  title: string
}

//initialState
export const rankingState = {
  displayType: 0,
  summaryExchange: 0,
  summaryType1: { type: {}, num: 0, time: "" },
  summaryType2: { type: {}, num: 1, time: "" },
  summaryType3: { type: {}, num: 2, time: "" },
  summaryType4: { type: {}, num: 3, time: "" },
  detailsExchange: 0,
  detailsType: { type: {}, num: 0, time: "" },
  sortStatus: { status: "neutral", param: "", title: "" },
}

//Reducer
export const rankingReducer = (state: RankingState, action: RankingAction) => {
  switch (action.actionType) {
    case "INITIAL":
      return {
        ...state,
        summaryType1: {
          type: action.rankingData[0].resultList,
          time: action.rankingData[0].serverTime,
          num: action.rankingTypeNum[0],
        },
        summaryType2: {
          type: action.rankingData[1].resultList,
          time: action.rankingData[1].serverTime,
          num: action.rankingTypeNum[1],
        },
        summaryType3: {
          type: action.rankingData[2].resultList,
          time: action.rankingData[2].serverTime,
          num: action.rankingTypeNum[2],
        },
        summaryType4: {
          type: action.rankingData[3].resultList,
          time: action.rankingData[3].serverTime,
          num: action.rankingTypeNum[3],
        },
        detailsType: {
          type: action.rankingData[4].resultList,
          time: action.rankingData[4].serverTime,
          num: 0,
        },
      }
    case "SMARTPHONE":
      return {
        ...state,
        displayType: 1,
        detailsExchange: action.exchange,
        detailsType: {
          type: action.rankingData.resultList,
          time: action.rankingData.serverTime,
          num: action.rankingTypeNum[0],
        },
      }
    case "DISPLAY_SUMMARY":
      return {
        ...state,
        displayType: 0,
        sortStatus: action.sortStatus,
      }
    case "DISPLAY_DETAILS":
      return {
        ...state,
        displayType: 1,
        sortStatus: action.sortStatus,
      }
    case "SUMMARY_EXCHANGE":
      return {
        ...state,
        displayType: 0,
        summaryExchange: action.exchange,
        summaryType1: {
          type: action.rankingData[0].resultList,
          time: action.rankingData[0].serverTime,
          num: action.rankingTypeNum[0],
        },
        summaryType2: {
          type: action.rankingData[1].resultList,
          time: action.rankingData[1].serverTime,
          num: action.rankingTypeNum[1],
        },
        summaryType3: {
          type: action.rankingData[2].resultList,
          time: action.rankingData[2].serverTime,
          num: action.rankingTypeNum[2],
        },
        summaryType4: {
          type: action.rankingData[3].resultList,
          time: action.rankingData[3].serverTime,
          num: action.rankingTypeNum[3],
        },
        detailsExchange: state.detailsExchange,
        sortStatus: action.sortStatus,
      }
    case "SUMMARY_EXCHANGE_TRANSITION":
      return {
        ...state,
        summaryExchange: action.exchange,
        summaryType1: {
          type: action.rankingData[0].resultList,
          time: action.rankingData[0].serverTime,
          num: action.rankingTypeNum[0],
        },
        summaryType2: {
          type: action.rankingData[1].resultList,
          time: action.rankingData[1].serverTime,
          num: action.rankingTypeNum[1],
        },
        summaryType3: {
          type: action.rankingData[2].resultList,
          time: action.rankingData[2].serverTime,
          num: action.rankingTypeNum[2],
        },
        summaryType4: {
          type: action.rankingData[3].resultList,
          time: action.rankingData[3].serverTime,
          num: action.rankingTypeNum[3],
        },
        detailsExchange: state.detailsExchange,
      }
    case "SUMMARY_TYPE_1":
      return {
        ...state,
        displayType: 0,
        summaryType1: {
          type: action.rankingData.resultList,
          time: action.rankingData.serverTime,
          num: action.rankingTypeNum[0],
        },
      }
    case "SUMMARY_TYPE_2":
      return {
        ...state,
        displayType: 0,
        summaryType2: {
          type: action.rankingData.resultList,
          time: action.rankingData.serverTime,
          num: action.rankingTypeNum[0],
        },
      }
    case "SUMMARY_TYPE_3":
      return {
        ...state,
        displayType: 0,
        summaryType3: {
          type: action.rankingData.resultList,
          time: action.rankingData.serverTime,
          num: action.rankingTypeNum[0],
        },
      }
    case "SUMMARY_TYPE_4":
      return {
        ...state,
        displayType: 0,
        summaryType4: {
          type: action.rankingData.resultList,
          time: action.rankingData.serverTime,
          num: action.rankingTypeNum[0],
        },
      }
    case "DETAILS_EXCHANGE":
      return {
        ...state,
        displayType: 1,
        detailsExchange: action.exchange,
        detailsType: {
          type: action.rankingData.resultList,
          time: action.rankingData.serverTime,
          num: action.rankingTypeNum[0],
        },
        sortStatus: action.sortStatus,
      }
    case "DETAILS_TYPE":
      return {
        ...state,
        displayType: 1,
        detailsType: {
          type: action.rankingData.resultList,
          time: action.rankingData.serverTime,
          num: action.rankingTypeNum[0],
        },
        sortStatus: action.sortStatus,
      }
    case "DETAILS_BUTTON":
      return {
        ...state,
        displayType: 1,
        detailsExchange: action.exchange,
        detailsType: {
          type: action.rankingData.resultList,
          time: action.rankingData.serverTime,
          num: action.rankingTypeNum[0],
        },
      }
    case "DETAILS_SORT":
      return {
        ...state,
        detailsType: {
          type: action.rankingData,
          time: get(state, "detailsType.time"),
          num: get(state, "detailsType.num"),
        },
        sortStatus: action.sortStatus,
      }
    default:
      return state
  }
}

//API
export const requestToRankingData = async (props: RequestApi) => {
  store.dispatch(pageLoadRegist(props.loadResistId, true))
  if (isArray(props.rankingType)) {
    const apiRes: APIResponse.Res = await Promise.all(
      map(props.rankingType, async (data, i) => {
        if (isNaN(data)) {
          return {
            resultList: nonData,
            serverTime: "",
          }
        }
        const ranking: APIResponse.Res | {} = await APIs.Tool.Ranking({
          Type: data,
          DayBefore: 0,
          _take: props.take[i],
        })
        const _resultList = get(ranking, "ResultList")
        const _serverTime = get(ranking, "ServerTime")
        return {
          resultList: _resultList,
          serverTime: _serverTime,
        }
      })
    )

    props.changeRanking({
      actionType: props.actionType,
      exchange: props.exchange,
      rankingData: apiRes,
      rankingTypeNum: props.rankingTypeNum,
    })
    store.dispatch(pageLoadRegist(props.loadResistId, false))
  } else {
    const ranking: APIResponse.Res | {} = await APIs.Tool.Ranking({
      Type: props.rankingType,
      DayBefore: 0,
      _take: props.take[0],
    })
    const param = get(props, "sortStatus.param")
    const orders = get(props, "sortStatus.status")
    //ポーリング時にソート崩れないように
    const _resultList = orderBy(
      get(ranking, "ResultList"),
      data => get(data, param),
      orders
    )
    const _serverTime = get(ranking, "ServerTime")
    const apiRes = {
      resultList: _resultList,
      serverTime: _serverTime,
    }
    props.changeRanking({
      actionType: props.actionType,
      exchange: props.exchange,
      rankingData: apiRes,
      rankingTypeNum: props.rankingTypeNum,
      sortStatus: props.sortStatus,
    })
    store.dispatch(pageLoadRegist(props.loadResistId, false))
  }
}

/************************
 * トップ＞ワールドサマリー
 * 外国株＞外国サマリー
 * 外国株＞各種外国株　　共通
 ***********************/

//Type:State
export type worldRankingState = {
  exchangeTypeNum: number
  priceIncreaseList: Array<object>
  priceReductionList: Array<object>
  volumeList: Array<object>
  priceIncreaseTime: string
  priceReductionTime: string
  volumeTime: string
}
//Type:Props
export type worldRankingProps = {
  exchangeTypeNum: number
  priceIncrease: number
  priceReduction: number
  volume: number
  changeRanking: Function
}

//API
export const worldRankingApi = async (props: worldRankingProps) => {
  const isPhone = checkIsSp()
  const types =
    props.exchangeTypeNum === IndustryNum
      ? [props.priceIncrease, props.priceReduction]
      : [props.priceIncrease, props.priceReduction, props.volume]

  const apiRes = await Promise.all(
    map(types, async data => {
      const ranking: APIResponse.Res | {} = await APIs.Tool.Ranking({
        Type: data,
        DayBefore: 0,
        _take: isPhone ? 5 : 3,
      })
      const _resultList = get(ranking, "ResultList")
      const _serverTime = get(ranking, "ServerTime")
      return {
        resultList: _resultList,
        // resultList: isEmpty(_resultList) ? nonData : _resultList,
        serverTime: _serverTime,
      }
    })
  )
  props.changeRanking({
    exchangeTypeNum: get(props, "exchangeTypeNum"),
    priceIncreaseList: get(apiRes, "[0].resultList"),
    priceReductionList: get(apiRes, "[1].resultList"),
    volumeList: get(apiRes, "[2].resultList"),
    priceIncreaseTime: get(apiRes, "[0].serverTime"),
    priceReductionTime: get(apiRes, "[1].serverTime"),
    volumeTime: get(apiRes, "[2].serverTime"),
  })
}
